import { Market, PeriodAggregation, TimeFrame } from '../types';
import { Entity, Periodicity } from '@ecosystem/newron-core/types';

const getAggregation = (aggregation: string | undefined, periodicity: Periodicity): PeriodAggregation => {
  if (aggregation) {
    return aggregation as PeriodAggregation;
  }

  return periodicity === Periodicity.Weekly ? PeriodAggregation.Week : PeriodAggregation.Month;
};

export const entityToTimeFrame = (entity: Entity): TimeFrame => {
  const [entityPeriodicity, period, periodAggregation] = entity.value.split(':');
  const periodicity = entityPeriodicity as Periodicity;

  const [startPeriod, endPeriod] = period.includes('-') ? period.split('-') : [undefined, period];

  const aggregation = getAggregation(periodAggregation, periodicity);

  return {
    aggregation,
    periodicity,
    endSequence: Number(endPeriod),
    startSequence: startPeriod ? Number(startPeriod) : undefined,
    label: entity.label,
    value: entity.value,
  };
};

// Food for thought: this function is different in BFF, is this correct in mfe?
export const timeFrameToEntity = (timeFrame: TimeFrame): Entity => {
  return {
    label: timeFrame.label,
    value: timeFrame.value,
  };
};

export const validateTimeFrame = (
  timeFrame: TimeFrame,
  forMarkets: Market[],
  withinTimeFrames: TimeFrame[]
): TimeFrame => {
  if (!hasPeriodicityInAllMarkets(forMarkets, timeFrame.periodicity)) {
    // probably weekly but not on all markets, default to a monthly time frame
    return getFallbackMonthlyTimeFrame(forMarkets, withinTimeFrames);
  }

  const lowestCommonSequence = getLowestCommonSequence(forMarkets, timeFrame.periodicity);

  if (Number(timeFrame.endSequence) > lowestCommonSequence) {
    return getAdjustedTimeFrame(timeFrame, withinTimeFrames, lowestCommonSequence);
  }

  return timeFrame;
};

const hasPeriodicityInAllMarkets = (markets: Market[], periodicity: Periodicity): boolean => {
  return markets.every(market => market.periods.some(period => period.periodicity === periodicity));
};

const getLowestCommonSequence = (markets: Market[], periodicity: Periodicity): number => {
  const allMatchingPeriods = markets.map(m => m.periods.find(p => p.periodicity === periodicity));
  return Math.min(...allMatchingPeriods.filter(Boolean).map(period => period.sequence));
};

const getFallbackMonthlyTimeFrame = (markets: Market[], availableTimeFrames: TimeFrame[]): TimeFrame => {
  const commonMonthlySequence = getLowestCommonSequence(markets, Periodicity.Monthly);
  return availableTimeFrames.find(
    timeFrame => timeFrame.aggregation === PeriodAggregation.Month && timeFrame.endSequence === commonMonthlySequence
  )!;
};

const getAdjustedTimeFrame = (
  original: TimeFrame,
  availableTimeFrames: TimeFrame[],
  maxSequence: number
): TimeFrame => {
  return availableTimeFrames.find(
    timeFrame => timeFrame.aggregation === original.aggregation && timeFrame.endSequence <= maxSequence
  )!;
};
